import React, { useEffect, useEffectEvent, useState } from 'react'
import { useLocation, useParams } from 'react-router'

import useApplicantTokenOnMount from '@sputnik/hooks/useApplicantTokenOnMount'
import useDivvyUUID from '@sputnik/hooks/useDivvyUUID'

import { useGetDecisionAndStatusForPolling } from './GetDecisionAndStatusForPolling.gql'
import ReviewingApplicationPollingPageContent from './ReviewingApplicationPollingPageContent'

const POLLING_MAX_TIMEOUT = 60000
const POLLING_RATE = 3000

const hasNavigableStatus = (action: string) => {
  switch (action) {
    case 'provide_documents':
    case 'provide_identity':
    case 'review_offer':
    case 'unfreeze_credit':
      return true
    default:
      return false
  }
}

const SubmittingApplicationPollingPage = () => {
  /* make sure we have a token before we start polling */
  const { appId } = useParams()
  useApplicantTokenOnMount({ salesforceCreditId: appId || '' })
  const { pathname, search: urlSearch } = useLocation()
  const [timer, setTimer] = useState(0)
  const [companyDivvyUuid] = useDivvyUUID()
  const { data, loading, startPolling, stopPolling } = useGetDecisionAndStatusForPolling({
    variables: {
      creditApplicationId: appId ?? '',
      companyId: companyDivvyUuid,
    },
    errorPolicy: 'all',
    pollInterval: POLLING_RATE,
    fetchPolicy: 'network-only',
  })
  const [isReadyToNavigate, setIsReadyToNavigate] = useState(false)
  const onReadyToNavigate = useEffectEvent(() => setIsReadyToNavigate(true))

  const { creditApplication, frozenAuthorizedSigner } = data ?? {}
  const { applicationActionItems } = creditApplication ?? {}

  const hasPollingTimedOut = timer >= POLLING_MAX_TIMEOUT
  const shouldNavigateImmediatelyStatus = applicationActionItems?.some(
    ({ action, completedAt }) => hasNavigableStatus(action) && !completedAt,
  )

  const finishedPolling =
    !!(hasPollingTimedOut || shouldNavigateImmediatelyStatus || frozenAuthorizedSigner) && !loading && !!data

  // save url to redirect back to it in case user leaves this page
  useEffect(() => {
    window.sessionStorage.setItem('submissionPendingUrl', pathname + urlSearch)
  }, [appId, pathname, urlSearch])

  // handle polling
  useEffect(() => {
    startPolling(POLLING_RATE)
    if (hasPollingTimedOut) {
      stopPolling()
      onReadyToNavigate()
    } else {
      setTimeout(() => setTimer(timer + POLLING_RATE), POLLING_RATE)
    }
  }, [hasPollingTimedOut, startPolling, stopPolling, timer])

  return (
    data && (
      <ReviewingApplicationPollingPageContent
        decisionAndStatusData={data}
        isReadyToNavigate={finishedPolling || isReadyToNavigate}
        stopPolling={() => stopPolling()}
      />
    )
  )
}

export default SubmittingApplicationPollingPage
